import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
    ensure,
    FormDropdown,
    FormMultiSelectList,
    FormValidationSummary,
    mapToDropdownOption,
    Stack,
    ValidationResult,
    validationSuccessful
} from "sprint-common-web";

import { CreateContentVersionButton } from "../../../../common/components/content-version/CreateContentVersionButton";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { isTrailerVersionType } from "../../../../common/types/TrailerContentVersion";
import { handleOptionChange } from "../../../../common/utils/handleOptionChange";
import { renderBrandedContentVersionText } from "../../../../common/utils/renderContentVersionText";
import { useAssociateContentFormContext } from "../context/useAssociateContentFormContext";
import { ensureTvAssociateDetails } from "../types/ContentAssociationDetails";
import { ContentAssociationFormState } from "../types/ContentAssociationFormState";
import { renderTrailerVersionInfo } from "../utils/renderTrailerVersionInfo";
import { createContentAssociationTvSeriesItemValidator } from "./ContentAssociationTvSeriesItemValidator";
import { useContentAssociationTvContentForm } from "./useContentAssociationTvContentForm";

export const ContentAssociationTvContentForm = (): JSX.Element => {
    const { control, errors, getValues } = useFormContext<ContentAssociationFormState>();
    const [episodeValidationResult, setEpisodeValidationResult] = useState<ValidationResult>();

    const { contentDetails, validation, setValidationResult, isEditDisabled } = useAssociateContentFormContext();

    ensure(contentDetails);
    ensureTvAssociateDetails(contentDetails);

    const validator = createContentAssociationTvSeriesItemValidator();

    const {
        init,
        opts,

        addNewVersion,

        selections: { clearEpisode, clearSeason, selectEpisode, selectSeason }
    } = useContentAssociationTvContentForm();

    useEffect(() => {
        init();

        const episodeId = getValues("episodeId");

        if (!episodeId) return;

        validateEpisode(episodeId);
    }, [contentDetails]);

    const validateEpisode = (episodeId: string) => {
        const episode = getEpisodeDetails(episodeId);

        if (!episode) return;

        const validationResult = validator.validateEpisode(episode);
        setEpisodeValidationResult(validationResult);
    };

    const shouldDisableVersionOpt = (itemId: string, selectedIds: string[]): boolean => {
        if (isEditDisabled) return true;

        const versions = getEpisodeVersions();

        if (!versions.length) return false;

        const selectedVersions = versions.filter((version) => selectedIds.includes(version.id));

        const versionToCheck = versions.find((v) => v.id === itemId);

        if (!selectedVersions.length || !versionToCheck) return false;

        return !validator.isMatchingVersion(versionToCheck, selectedVersions);
    };

    const validateEpisodeVersion = (itemId: string): ValidationResult => {
        const versions = getEpisodeVersions();

        if (!versions.length) return validationSuccessful();

        const versionToValidate = versions.find((v) => v.id === itemId);

        if (!versionToValidate) return validationSuccessful();

        return validator.validateVersion(versionToValidate, versions);
    };

    const getEpisodeDetails = (episodeId: string) => {
        const seasonId = getValues("seasonId");

        if (!seasonId) return;

        const season = opts.seasons.find((s) => s.id === seasonId);

        if (!season) return;

        return season.episodes.find((e) => e.id === episodeId);
    };

    const getEpisodeVersions = () => {
        const episodeId = getValues("episodeId");

        if (!episodeId) return [];

        const episode = getEpisodeDetails(episodeId);

        if (!episode) return [];

        return episode.versions;
    };

    const validateSelectedVersions = (versionIds: string[]): ValidationResult => {
        return validation.actions?.validateVersions?.(opts.versions, versionIds) ?? validationSuccessful();
    };

    const createContentVersionButtonView = (
        <Stack style={{ marginLeft: "5px" }}>
            <CreateContentVersionButton
                disabled={!opts.versions.length || isEditDisabled}
                content={{
                    type: ContentReferenceType.TvSeries,
                    tvSeriesId: contentDetails.id,
                    episodeId: getValues("episodeId") ?? ""
                }}
                onContentVersionCreated={addNewVersion}
            />
        </Stack>
    );

    const shouldRenderTrailerInfo = (itemId: string) => {
        const versions = getEpisodeVersions();

        if (!versions.length) return false;

        const versionToCheck = versions.find((v) => v.id === itemId);

        return isTrailerVersionType(versionToCheck?.type);
    };

    const renderAdditionalInfo = (itemId: string, isItemInactive?: boolean) => {
        if (!shouldRenderTrailerInfo(itemId)) {
            return <></>;
        }

        return renderTrailerVersionInfo({ isItemInactive });
    };

    return (
        <>
            <FormDropdown
                options={opts.seasons.map((opt) => mapToDropdownOption(opt))}
                onChange={handleOptionChange(selectSeason, clearSeason)}
                control={control}
                errors={errors}
                name="seasonId"
                rules={{
                    required: validation.rules.isSeasonRequired ? "Search and select season" : false
                }}
                placeholder="Season"
                label="Season"
                disabled={!opts.seasons.length || isEditDisabled}
            />

            <FormDropdown
                options={opts.episodes.map((opt) => mapToDropdownOption(opt))}
                onChange={handleOptionChange(selectEpisode, clearEpisode)}
                control={control}
                errors={errors}
                name="episodeId"
                rules={{
                    required: validation.rules.isEpisodeRequired ? "Search and select episode" : false
                }}
                placeholder="Episode"
                label="Episode"
                disabled={!opts.episodes.length || isEditDisabled}
            />
            <FormMultiSelectList
                items={opts.versions.map((opt) => mapToDropdownOption(opt, () => renderBrandedContentVersionText(opt)))}
                isItemDisabled={shouldDisableVersionOpt}
                onChange={() => setValidationResult(undefined)}
                onItemValidating={validateEpisodeVersion}
                renderAdditionalInfo={renderAdditionalInfo}
                control={control}
                errors={errors}
                actionButtons={[createContentVersionButtonView]}
                name="versionIds"
                rules={{
                    required: validation.rules.areVersionsRequired ? "Select versions" : false,
                    validate: validateSelectedVersions
                }}
                label="Versions"
                disabled={!opts.versions.length || isEditDisabled}
                maxHeight={95}
            />
            <FormValidationSummary validationResult={episodeValidationResult} />
        </>
    );
};
